import React, { useState, useEffect } from "react";
import {
	Box,
	Container,
	Typography,
	Stack,
	Chip,
	Button,
	IconButton,
	useTheme,
	useMediaQuery,
} from "@mui/material";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Link } from "react-router-dom";
import { DATA } from "./ProjectsData";
import FooterSection from "../../components/FooterSection/FooterSection";
import ImageCarousel from "../../components/ImageCarousel";
// import NorthSouthIcon from "@mui/icons-material/NorthSouth";

/** Palette tuned to Figma */
const COLORS = {
	bg: "#FCFBF8",
	brown: "#674934",
	brownDark: "#533A29",
	border: "#D9CFC7",
	placeholder: "#E9E4DE",
};

const CHIP_STYLES = {
	base: {
		height: 52, // Figma
		borderRadius: 32,
		px: 2.5,
		py: 0,
		fontFamily: "'Poppins', sans-serif",
		fontSize: 24,
		fontWeight: 400,
		lineHeight: "24px",
		letterSpacing: 0.2,
		borderWidth: 1,
		textTransform: "none",
		boxShadow: "none",
		"& .MuiChip-label": {
			px: 0,
			display: "inline-flex",
			alignItems: "center",
		},
	},
	outlined: (color) => ({
		color,
		borderColor: color,
		bgcolor: "transparent",
		"&:hover": { bgcolor: "#F5F1EC" },
	}),
	filled: (color) => ({
		color: "#fff",
		bgcolor: color,
		borderColor: color,
		"&:hover": { bgcolor: "#533A29" },
	}),
};

export default function ProjectsPage() {
	const [filter, setFilter] = useState("All");
	const [menuOpen, setMenuOpen] = useState(false);
	const [mobileSortOpen, setMobileSortOpen] = useState(false);
	const [mobileDetailProject, setMobileDetailProject] = useState(null);

	const theme = useTheme();
	// Desktop only on lg and up (so iPad landscape = mobile layout)
	const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

	// 🔹 Scroll to top whenever this page mounts
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const residentialProjects = DATA.filter((p) => p.type === "Residential");
	const commercialProjects = DATA.filter((p) => p.type === "Commercial");

	const currentHeading =
		filter === "Residential"
			? "Residential Projects"
			: filter === "Commercial"
			? "Commercial Projects"
			: "All Projects";

	const handleMobileReadMore = (project) => {
		setMobileDetailProject(project);
	};

	const handleSortChange = (value) => {
		setFilter(value);
		setMobileSortOpen(false);
	};

	return (
		<Box
			sx={{
				backgroundImage: "url(/paper tecture 1.png)",
				backgroundAttachment: "fixed",
				backgroundSize: "cover",
				backgroundRepeat: "repeat",
				backgroundPosition: "top left",
				minHeight: "100vh",
				overflowX: "hidden", // prevent horizontal scroll
			}}
		>
			{/* ===== Fixed Header ===== */}
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					zIndex: 1200,
					bgcolor: "rgba(252, 251, 248, 0.95)",
					borderBottom: `1px solid ${COLORS.border}`,
					backdropFilter: "blur(10px)",
				}}
			>
				<Container
					maxWidth="xl"
					sx={{
						px: { xs: 2, sm: 3, lg: 6 },
						pt: { xs: 1.5, lg: 3 },
						pb: { xs: 1.5, lg: 2 },
					}}
				>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
					>
						{/* Logo image heading (matches navbar logo) */}
						<Box
							component={Link}
							to="/"
							sx={{
								display: "inline-flex",
								alignItems: "center",
								textDecoration: "none",
								lineHeight: 0,
							}}
						>
							<Box
								component="img"
								src="/images/Saffron_House.png"
								alt="Saffron House"
								sx={{
									height: { xs: 40, lg: 52 },
									width: "auto",
									display: "block",
								}}
							/>
						</Box>

						{/* Desktop buttons */}
						<Stack
							direction="row"
							spacing={1.5}
							sx={{ display: { xs: "none", lg: "flex" } }}
						>
							<Button
								variant="contained"
								disableElevation
								sx={{
									bgcolor: COLORS.brown,
									"&:hover": { bgcolor: COLORS.brownDark },
									color: "#fff",
									borderRadius: 999,
									px: 2.5,
									height: 52,
									fontSize: 24,
									textTransform: "none",
									fontFamily: "'Poppins', sans-serif",
									fontWeight: 500,
								}}
							>
								Projects
							</Button>
							<Button
								variant="outlined"
								endIcon={<ArrowOutwardIcon fontSize="small" />}
								component="a"
								href="https://wa.me/917000758077"
								target="_blank"
								rel="noopener noreferrer"
								sx={{
									color: COLORS.brown,
									borderColor: COLORS.brown,
									"&:hover": {
										borderColor: COLORS.brownDark,
										bgcolor: "#F5F1EC",
									},
									borderRadius: 999,
									px: 2.5,
									height: 52,
									fontSize: 20,
									textTransform: "none",
									fontFamily: "'Poppins', sans-serif",
									fontWeight: 500,
								}}
							>
								Contact Us
							</Button>
						</Stack>

						{/* Mobile menu icon */}
						<IconButton
							onClick={() => setMenuOpen((s) => !s)}
							sx={{
								display: { xs: "inline-flex", lg: "none" },
								color: COLORS.brown,
							}}
							aria-label="menu"
						>
							{menuOpen ? <CloseIcon /> : <MenuIcon />}
						</IconButton>
					</Stack>
				</Container>
			</Box>

			{/* Spacer so content starts below fixed header */}
			<Box sx={{ height: { xs: 76, lg: 96 } }} />

			{/* ===== Wide Video Section (same rail as landing hero video) ===== */}
			<Container
				maxWidth={false}
				sx={{
					width: { xs: "100%", md: "95%" }, // same as HeroSection
					maxWidth: 1500, // same as HeroSection
					mx: "auto",
					px: { xs: 1, md: 0 }, // same as HeroSection
					mb: 4,
				}}
			>
				<Box
					sx={{
						position: "relative",
						width: "100%",
						borderRadius: { xs: 0, lg: 2 },
						overflow: "hidden",
						bgcolor: COLORS.placeholder,
						border: {
							xs: "none",
							lg: `1px solid ${COLORS.border}`,
						},
					}}
				>
					<Box
						component="video"
						autoPlay
						muted
						loop
						playsInline
						src="/images/projects-page-video.mp4"
						sx={{
							width: "100%",
							height: {
								xs: 260, // mobile
								sm: 320,
								md: 420,
								lg: 480,
								xl: 580,
							},
							objectFit: "cover",
							display: "block",
						}}
					/>
				</Box>
			</Container>

			{/* ===== Body ===== */}
			<Container
				maxWidth={false}
				sx={{
					// ✅ same left/right rail as the video
					width: { xs: "100%", md: "95%" },
					maxWidth: 1500,
					mx: "auto",
					px: { xs: 1, md: 0 },
					pb: { xs: 6, lg: 10 },
				}}
			>
				{/* Desktop filter chips */}
				<Stack
					direction="row"
					spacing={1.5}
					mb={4}
					sx={{
						justifyContent: { xs: "center", lg: "flex-start" },
						display: { xs: "none", lg: "flex" },
					}}
				>
					{["All", "Residential", "Commercial"].map((val) => {
						const active = filter === val;
						return (
							<Chip
								key={val}
								clickable
								label={val}
								onClick={() => setFilter(val)}
								variant={active ? "filled" : "outlined"}
								sx={{
									...CHIP_STYLES.base,
									...(active
										? CHIP_STYLES.filled(COLORS.brown)
										: CHIP_STYLES.outlined(COLORS.brown)),
								}}
							/>
						);
					})}
				</Stack>

				{/* Mobile heading + sort-by button + inline dropdown */}
				<Box
					sx={{
						display: { xs: "flex", lg: "none" },
						alignItems: "center",
						justifyContent: "space-between",
						mb: 3,
						position: "relative",
						zIndex: 5,
					}}
				>
					<Typography
						sx={{
							fontFamily: "'Lora', serif",
							fontWeight: 500,
							// 🔹 Slightly larger on tablets
							fontSize: { xs: 24, sm: 26, md: 28 },
							color: COLORS.brown,
						}}
					>
						{currentHeading}
					</Typography>

					<Box sx={{ position: "relative" }}>
						<Button
							variant="outlined"
							size="small"
							onClick={() => setMobileSortOpen((prev) => !prev)}
							endIcon={<SortArrowsIcon />}
							sx={{
								borderRadius: 999,
								textTransform: "none",
								fontFamily: "'Poppins', sans-serif",
								// 🔹 bump on tablets
								fontSize: { xs: 12, sm: 13, md: 14 },
								paddingInline: { xs: 2, sm: 2.5, md: 3 },
								borderColor: COLORS.brown,
								color: COLORS.brown,
								"& .MuiButton-endIcon": {
									ml: 0.75,
									mr: 0,
								},
								"&:hover": {
									borderColor: COLORS.brownDark,
									bgcolor: "#F5F1EC",
								},
							}}
						>
							sort by
						</Button>

						{/* Dropdown popup under sort-by (mobile only) */}
						{mobileSortOpen && (
							<Box
								sx={{
									position: "absolute",
									top: "100%",
									right: 0,
									mt: 1,
									bgcolor: COLORS.brown,
									borderRadius: 4, // tighter radius
									overflow: "hidden",
									boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.16)",
									minWidth: 174,
								}}
							>
								<Button
									onClick={() => handleSortChange("All")}
									sx={dropdownItemSx(filter === "All", true)}
								>
									All Projects
								</Button>
								<Button
									onClick={() => handleSortChange("Residential")}
									sx={dropdownItemSx(filter === "Residential", true)}
								>
									Residential Projects
								</Button>
								<Button
									onClick={() => handleSortChange("Commercial")}
									sx={dropdownItemSx(filter === "Commercial", false)}
								>
									Commercial Projects
								</Button>
							</Box>
						)}
					</Box>
				</Box>

				{/* Category headings + project groups */}
				{filter === "All" && (
					<>
						{isLgUp ? (
							// Desktop: separate groups + headings
							<>
								<CategoryHeading label="Residential Projects" />
								<ProjectsGroup
									projects={residentialProjects}
									onMobileReadMore={handleMobileReadMore}
								/>

								<CategoryHeading
									label="Commercial Projects"
									sx={{ mt: { xs: 4, lg: 8 } }}
								/>
								<ProjectsGroup
									projects={commercialProjects}
									onMobileReadMore={handleMobileReadMore}
								/>
							</>
						) : (
							// Mobile / tablet: one flat list so spacing is uniform
							<ProjectsGroup
								projects={[...residentialProjects, ...commercialProjects]}
								onMobileReadMore={handleMobileReadMore}
							/>
						)}
					</>
				)}

				{filter === "Residential" && (
					<ProjectsGroup
						projects={residentialProjects}
						onMobileReadMore={handleMobileReadMore}
					/>
				)}

				{filter === "Commercial" && (
					<ProjectsGroup
						projects={commercialProjects}
						onMobileReadMore={handleMobileReadMore}
					/>
				)}
			</Container>

			{/* Mobile Project Detail Overlay (brown background, cream text) */}
			{mobileDetailProject && (
				<Box
					sx={{
						position: "fixed",
						inset: 0,
						bgcolor: COLORS.brown,
						color: COLORS.bg,
						zIndex: 1400,
						display: "flex",
						flexDirection: "column",
					}}
				>
					{/* Top FIXED section – image + title + subtitle + underline */}
					<Box sx={{ flexShrink: 0 }}>
						<Container maxWidth="sm" sx={{ pt: 3, pb: 0 }}>
							{/* Image carousel on top, rounded, NO white border */}
							<Box
								sx={{
									borderRadius: 3,
									overflow: "hidden",
									mb: 2,
								}}
							>
								<ImageCarousel
									images={
										mobileDetailProject.images || [mobileDetailProject.image]
									}
									title={mobileDetailProject.title}
								/>
							</Box>{" "}
							{/* Title, subtitle, underline */}
							<Box sx={{ mb: 1.5 }}>
								<Typography
									sx={{
										fontFamily: "'Lora', serif",
										fontWeight: 400,
										fontSize: 20,
										lineHeight: "26px",
									}}
								>
									{mobileDetailProject.title}
								</Typography>

								{mobileDetailProject.subtitle && (
									<Typography
										sx={{
											mt: 0.25,
											fontFamily: "'Lora', serif",
											fontWeight: 400,
											fontSize: 14,
										}}
									>
										{mobileDetailProject.subtitle}
									</Typography>
								)}

								<Box
									sx={{
										mt: 1,
										height: 1.1,
										width: "100%",
										bgcolor: COLORS.bg,
										opacity: 1,
									}}
								/>
							</Box>
						</Container>
					</Box>

					{/* MIDDLE SCROLLABLE section – tagline + meta + sections */}
					<Box
						sx={{
							flex: 1,
							overflowY: "auto",
						}}
					>
						<Container
							maxWidth="sm"
							sx={{
								pt: 0,
								pb: 14, // space so last text not hidden behind button
							}}
						>
							{/* Tagline */}
							{mobileDetailProject.tagline && (
								<Typography
									sx={{
										mt: 1,
										mb: 2,
										fontFamily: "'Poppins', sans-serif",
										fontWeight: 700,
										fontSize: 12,
									}}
								>
									{mobileDetailProject.tagline}
								</Typography>
							)}

							{/* Meta items */}
							<Stack spacing={1} sx={{ mb: 2 }}>
								<MetaItem
									label="Client"
									value={mobileDetailProject.client}
									color={COLORS.bg}
								/>
								<MetaItem
									label="Location"
									value={mobileDetailProject.location}
									color={COLORS.bg}
								/>
								<MetaItem
									label="Project Duration"
									value={mobileDetailProject.duration}
									color={COLORS.bg}
								/>
							</Stack>

							{/* The Space / Ask / Result sections */}
							<Box className="sections">
								<SectionBlock
									label="The Space"
									text={mobileDetailProject.space}
									color={COLORS.bg}
								/>
								<SectionBlock
									label="The Ask"
									text={mobileDetailProject.ask}
									color={COLORS.bg}
								/>
								<SectionBlock
									label="The Result"
									text={mobileDetailProject.result}
									color={COLORS.bg}
								/>
							</Box>
						</Container>
					</Box>

					{/* Bottom FIXED Close button */}
					<Box
						sx={{
							flexShrink: 0,
							px: 2,
							pb: 2,
							pt: 1,
						}}
					>
						<Button
							fullWidth
							onClick={() => setMobileDetailProject(null)}
							sx={{
								borderRadius: 1,
								textTransform: "none",
								fontFamily: "'Poppins', sans-serif",
								fontSize: 14,
								fontWeight: 500,
								bgcolor: COLORS.bg,
								color: COLORS.brown,
								"&:hover": {
									bgcolor: "#F0E9DF",
								},
							}}
						>
							Close
						</Button>
					</Box>
				</Box>
			)}

			{/* Mobile hamburger menu – full brown background with logo + close */}
			{menuOpen && (
				<Box
					sx={{
						position: "fixed",
						inset: 0,
						bgcolor: COLORS.brown,
						color: COLORS.bg,
						zIndex: 1300,
						display: { xs: "block", lg: "none" },
					}}
				>
					<Container
						maxWidth="xl"
						disableGutters
						sx={{ px: 2.5, py: 3, width: "100%" }}
					>
						{/* Top row: logo + close */}
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								mb: 4,
							}}
						>
							<Box
								component={Link}
								to="/"
								onClick={() => setMenuOpen(false)}
								sx={{
									display: "inline-flex",
									alignItems: "center",
									textDecoration: "none",
									lineHeight: 0,
								}}
							>
								<Box
									component="img"
									src="/images/Saffron_House.png"
									alt="Saffron House"
									sx={{
										height: 32,
										width: "auto",
										display: "block",
										filter: "brightness(0) invert(1)", // ensure white logo if needed
									}}
								/>
							</Box>

							<IconButton
								onClick={() => setMenuOpen(false)}
								sx={{ color: COLORS.bg }}
								aria-label="close menu"
							>
								<CloseIcon sx={{ fontSize: 32 }} />
							</IconButton>
						</Box>

						{/* Menu items */}
						<Stack spacing={2.5}>
							<Button
								component={Link}
								to="/projects"
								onClick={() => setMenuOpen(false)}
								variant="text"
								disableRipple
								sx={{
									justifyContent: "flex-start",
									px: 0,
									minWidth: 0,
									textTransform: "none",
									fontFamily: "'Poppins', sans-serif",
									fontSize: 24,
									color: COLORS.bg,
									"&:hover": {
										bgcolor: "transparent",
										opacity: 0.8,
									},
								}}
							>
								Projects
							</Button>

							<Button
  component="a"
  href="https://wa.me/917000758077"
  target="_blank"
  rel="noopener noreferrer"
  onClick={() => setMenuOpen(false)}
  variant="text"
  disableRipple
  sx={{
    justifyContent: "flex-start",
    px: 0,
    minWidth: 0,
    textTransform: "none",
    fontFamily: "'Poppins', sans-serif",
    fontSize: 24,
    color: COLORS.bg,
    "& .MuiButton-endIcon": {
      ml: 1,
    },
    "& .MuiButton-endIcon svg": {
      fontSize: 32,
    },
    "&:hover": {
      bgcolor: "transparent",
      opacity: 0.8,
    },
  }}
  endIcon={<ArrowOutwardIcon />}
>
  Contact Us
</Button>

						</Stack>
					</Container>
				</Box>
			)}
			<FooterSection />
		</Box>
	);
}

/* ---- small helpers ---- */

function CategoryHeading({ label, sx }) {
	return (
		<Typography
			sx={{
				fontFamily: "'Lora', serif",
				fontWeight: { xs: 500, lg: 500 },
				fontSize: { xs: 20, lg: 44 },
				lineHeight: { xs: "32px", lg: "50px" },
				color: COLORS.brown,
				mb: { xs: 2, lg: 3 },
				...sx,
			}}
		>
			{label}
		</Typography>
	);
}

function ProjectsGroup({ projects, onMobileReadMore }) {
	return (
		<Stack spacing={{ xs: 4, lg: 8 }}>
			{projects.map((p, idx) => (
				<Box key={`${p.title}-${idx}`}>
					{/* Desktop row */}
					<Box
						sx={{
							display: { xs: "none", md: "grid" },
							gridTemplateColumns: { md: "1fr 1fr", lg: "1fr 1fr" },
							columnGap: { md: 2, lg: 2 },
							rowGap: { md: 0, lg: 0 },
							alignItems: "stretch",
						}}
					>
						{/* Left visual */}
						<ImageCarousel images={p.images} title={p.title} />

						{/* Right text */}
						<Box
							sx={{
								p: 0,
								pt: 0,
								"& .sections > :last-child": { mb: 0 },
							}}
						>
							<Box sx={{ mb: 2 }}>
								<Box
									sx={{
										display: "flex",
										alignItems: "baseline",
										gap: 1,
										flexWrap: "wrap",
									}}
								>
									<Typography
										component="span"
										sx={{
											fontFamily: "'Lora', serif",
											fontWeight: 500,
											fontSize: { xs: 28, lg: 44 },
											lineHeight: { xs: "42px", lg: "50px" },
											color: COLORS.brown,
											marginBottom: -1,
										}}
									>
										{p.title}
									</Typography>

									{!!p.subtitle && (
										<Typography
											component="span"
											sx={{
												ml: 0.5,
												fontFamily: "'Poppins', sans-serif",
												fontWeight: 600,
												fontSize: 16,
												color: COLORS.brown,
											}}
										>
											{p.subtitle}
										</Typography>
									)}
								</Box>

								<Box
									sx={{
										display: "inline-block",
										pb: 0.1,
										mt: 1,
										height: 0.1,
										bgcolor: COLORS.brown,
										width: { xs: "95%", lg: "80%" },
										borderRadius: 2,
									}}
								/>
							</Box>

							{p.tagline && (
								<Typography
									sx={{
										mb: 1,
										color: COLORS.brown,
										fontWeight: 600,
										fontFamily: "'Poppins', sans-serif",
									}}
								>
									{p.tagline}
								</Typography>
							)}

							<Stack spacing={1} sx={{ mb: 1 }}>
								<MetaItem label="Client" value={p.client} />
								<MetaItem label="Location" value={p.location} />
								<MetaItem label="Project Duration" value={p.duration} />
							</Stack>

							<Box className="sections">
								<SectionBlock label="The Space" text={p.space} />
								<SectionBlock label="The Ask" text={p.ask} />
								<SectionBlock label="The Result" text={p.result} />
							</Box>
						</Box>
					</Box>

					{/* Mobile card – only image + title/subtitle + read more */}
					<Box sx={{ display: { xs: "block", md: "none" } }}>
						<ImageCarousel images={p.images || [p.image]} title={p.title} />
						<Box
							sx={{
								mt: 1,
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								gap: 1,
							}}
						>
							<Box>
								<Typography
									sx={{
										fontFamily: "'Lora', serif",
										fontWeight: 500,
										// 🔹 slightly larger for tablets
										fontSize: { xs: 18, sm: 20, md: 22 },
										color: COLORS.brown,
									}}
								>
									{p.title}
								</Typography>
								{!!p.subtitle && (
									<Typography
										sx={{
											fontFamily: "'Poppins', sans-serif",
											// 🔹 slightly larger for tablets
											fontSize: { xs: 10, sm: 11, md: 12 },
											color: COLORS.brown,
										}}
									>
										{p.subtitle.replace(/^-+\s*/, "")}
									</Typography>
								)}
							</Box>
							<Button
								variant="outlined"
								size="small"
								onClick={() => onMobileReadMore(p)}
								sx={{
									borderRadius: 999,
									textTransform: "none",
									fontFamily: "'Poppins', sans-serif",
									// 🔹 slightly larger on tablets
									fontSize: { xs: 10, sm: 11, md: 12 },
									paddingInline: { xs: 2, sm: 2.5, md: 3 },
									borderColor: COLORS.brown,
									color: COLORS.brown,
									"&:hover": {
										borderColor: COLORS.brownDark,
										bgcolor: "#F5F1EC",
									},
								}}
							>
								read more
							</Button>
						</Box>
					</Box>
				</Box>
			))}
		</Stack>
	);
}

function MetaItem({ label, value, color = COLORS.brown }) {
	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "flex-start",
				columnGap: 0.75,
				flexWrap: "wrap",
				mb: 0.25,
			}}
		>
			<Typography
				variant="body1"
				sx={{
					fontFamily: "'Poppins', sans-serif",
					fontSize: { xs: 14, lg: 14 },
					fontWeight: 600,
					color,
					whiteSpace: "nowrap", // keeps "Location :" together
				}}
			>
				{label} :
			</Typography>

			<Typography
				variant="body1"
				sx={{
					fontFamily: "'Poppins', sans-serif",
					color,
					lineHeight: 1.3,
					fontWeight: 400,
					flex: 1,
					minWidth: 0,
					wordBreak: "break-word", // long locations wrap nicely
				}}
			>
				{value}
			</Typography>
		</Box>
	);
}

function SectionBlock({ label, text, color = COLORS.brown }) {
	if (!text) return null;
	return (
		<Box sx={{ mb: 3 }}>
			<Typography
				variant="body1"
				sx={{
					fontFamily: "'Poppins', sans-serif",
					fontWeight: 600,
					color,
					mb: 0.25,
				}}
			>
				{label}
			</Typography>
			<Typography
				variant="body1"
				sx={{
					fontFamily: "'Poppins', sans-serif",
					color,
					lineHeight: 1.25,
					fontWeight: 400,
				}}
			>
				{text}
			</Typography>
		</Box>
	);
}

/** Styles for dropdown items in mobile sort popup */
function dropdownItemSx(active, showDivider) {
	return {
		justifyContent: "flex-start",
		alignItems: "center",
		borderRadius: 0,
		textTransform: "none",
		fontFamily: "'Poppins', sans-serif",
		fontSize: 13,
		fontWeight: 600,
		px: 2.5,
		py: 1.1,
		color: "#fff",

		// 🔹 make all labels single-line and left aligned
		minWidth: 210, // a bit wider so “Residential Projects” fits
		textAlign: "left",
		whiteSpace: "nowrap", // prevent wrapping

		borderBottom: showDivider ? "1px solid rgba(255,255,255,0.35)" : "none",
		bgcolor: active ? COLORS.brownDark : COLORS.brown,
		"&:hover": {
			bgcolor: COLORS.brownDark,
		},
	};
}

function SortArrowsIcon(props) {
	return (
		<svg
			viewBox="0 0 24 24"
			width={18}
			height={18}
			fill="none"
			aria-hidden="true"
			{...props}
		>
			{/* Up arrow */}
			<path fill="currentColor" d="M8 4l-4 4h3v8h2V8h3L8 4z" />
			{/* Down arrow */}
			<path fill="currentColor" d="M16 20l4-4h-3V8h-2v8h-3l4 4z" />
		</svg>
	);
}
